import { z } from "zod";
/**
 * Money representation for Accounting, deliberately mirroring the
 * conventions already established elsewhere rather than inventing a new
 * one (Phase 7 task brief): integer minor units plus an explicit ISO 4217
 * currency, never a JavaScript float
 * (`packages/database/AGENTS.md`: "Store ... money in integer minor units
 * plus currency"; `packages/marketplace`'s `priceMinor`;
 * `packages/planner/src/money.ts`'s `purseMinor`; ADR 0014 "Exact amount
 * conversion": "never uses JavaScript floating-point arithmetic and never
 * rounds or truncates an entered value").
 *
 * This module intentionally duplicates the *shape* of
 * `packages/planner/src/money.ts` rather than importing it: Planner and
 * Stable are separate Products (`AGENTS.md` §6 "Import another workspace
 * through its public package export ... Do not deep-import another
 * Product or package's internals"), and ADR 0014 is scoped narrowly to
 * Planner's CSV/purse contract. Promoting this into a shared cross-Product
 * money package is a real option but needs its own ADR and is out of
 * scope for this phase — see the Phase 7 completion report.
 */
export const CurrencyCodeSchema = z
    .string()
    .trim()
    .length(3)
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z]{3}$/.test(value), "currency must be 3 letters.");
/** Digit-string integer minor units — never a JS `number`, to avoid float
 * precision loss on large invoice totals, mirroring `priceMinor`'s
 * `/^[1-9]\d{0,14}$/` shape in `packages/marketplace/src/public-validation.ts`.
 * Allocation/line-item amounts may legitimately be `0` (e.g. a horse
 * allocated 0% of a bill by explicit choice), so this allows a lone `0`
 * unlike Marketplace's strictly-positive listing price. */
export const AmountMinorSchema = z
    .string()
    .regex(/^(0|[1-9]\d{0,14})$/, "amount must be a non-negative integer.");
const MAX_AMOUNT_MINOR = 9223372036854775807n; // PostgreSQL bigint max.
/** Currencies with 0 or 3 minor-unit digits — the ISO 4217 exceptions to the
 * "2 decimal places" default. Copied from `packages/planner/src/money.ts`
 * (public ISO 4217 facts, not Planner-proprietary data) since Stable
 * deliberately does not import Planner's module (see module doc comment
 * above). AED, this phase's only UI-exposed currency, uses the 2-digit
 * default. */
const ZERO_MINOR_UNIT_CURRENCIES = new Set([
    "BIF",
    "CLP",
    "DJF",
    "GNF",
    "ISK",
    "JPY",
    "KMF",
    "KRW",
    "PYG",
    "RWF",
    "UGX",
    "VND",
    "VUV",
    "XAF",
    "XOF",
    "XPF",
]);
const THREE_MINOR_UNIT_CURRENCIES = new Set([
    "BHD",
    "IQD",
    "JOD",
    "KWD",
    "LYD",
    "OMR",
    "TND",
]);
export function currencyMinorUnitDigits(currency) {
    if (ZERO_MINOR_UNIT_CURRENCIES.has(currency))
        return 0;
    if (THREE_MINOR_UNIT_CURRENCIES.has(currency))
        return 3;
    return 2;
}
const MAJOR_AMOUNT_PATTERN = /^(\d+)(?:\.(\d{1,3}))?$/u;
/**
 * Converts a human-entered major-unit decimal amount (what a bill/receipt
 * actually shows, e.g. "45.50") into the canonical integer-minor-unit
 * string — exact string/`bigint` arithmetic only, mirroring
 * `plannerPurseAmountToMinor` (ADR 0014: "the whole part is scaled by the
 * currency's power of ten and the fractional part is right-padded to that
 * precision"). Returns `null` for a malformed amount, a fractional part
 * exceeding the currency's precision, or an amount too large to persist.
 */
export function majorAmountToMinor(amount, currency) {
    const minorUnitDigits = currencyMinorUnitDigits(currency);
    const match = MAJOR_AMOUNT_PATTERN.exec(amount.trim());
    if (match?.[1] === undefined)
        return null;
    const whole = match[1].replace(/^0+(?=\d)/u, "");
    if (whole.length > 18)
        return null;
    const fraction = match[2];
    if ((minorUnitDigits === 0 && fraction !== undefined) ||
        (fraction?.length ?? 0) > minorUnitDigits) {
        return null;
    }
    const scale = 10n ** BigInt(minorUnitDigits);
    const paddedFraction = (fraction ?? "").padEnd(minorUnitDigits, "0");
    const minor = BigInt(whole) * scale +
        (paddedFraction.length === 0 ? 0n : BigInt(paddedFraction));
    if (minor > MAX_AMOUNT_MINOR)
        return null;
    const parsed = AmountMinorSchema.safeParse(minor.toString());
    return parsed.success ? parsed.data : null;
}
/** Inverse of {@link majorAmountToMinor}, for display — mirrors
 * `plannerPurseMinorToAmount`. */
export function minorAmountToMajor(amountMinor, currency) {
    const minorUnitDigits = currencyMinorUnitDigits(currency);
    const minor = BigInt(amountMinor);
    if (minorUnitDigits === 0)
        return minor.toString();
    const scale = 10n ** BigInt(minorUnitDigits);
    const whole = minor / scale;
    const fraction = (minor % scale).toString().padStart(minorUnitDigits, "0");
    return `${whole}.${fraction}`;
}
/** Exact `bigint` sum of a set of minor-unit amounts, never a floating-point
 * addition — used to compute invoice/bill totals. */
export function sumAmountsMinor(amounts) {
    const total = amounts.reduce((sum, amount) => sum + BigInt(amount), 0n);
    const parsed = AmountMinorSchema.safeParse(total.toString());
    if (!parsed.success) {
        throw new RangeError("Summed amount exceeds the persistable range.");
    }
    return parsed.data;
}
